import api from './authService';

export interface UserStory {
    id: string;
    requirementId: string;
    projectId: string;
    title: string;
    description: string;
    acceptanceCriteria: string | string[];
    persona: string;
    priority: 'low' | 'medium' | 'high';
    storyPoints: number;
    status: 'backlog' | 'in_progress' | 'review' | 'done' | 'blocked';
    assignedTo?: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    requirement?: any;
    assignee?: any;
    creator?: any;
    tasks?: any[];
    integrationLinks?: {
        id: string;
        provider: string;
        externalId: string;
        externalUrl: string;
    }[];
}

export interface GenerateUserStoriesRequest {
    requirementId: string;
    extraContext?: string;
    regenerate?: boolean;
    count?: number;
}

class UserStoryService {
    /**
     * Get all user stories (with optional filters)
     */
    async getUserStories(params?: {
        requirementId?: string;
        projectId?: string;
        search?: string;
        page?: number;
        limit?: number;
    }) {
        const response = await api.get('/user-stories', { params });
        return response.data;
    }

    /**
     * Get a single user story by ID
     */
    async getUserStory(id: string) {
        const response = await api.get(`/user-stories/${id}`);
        return response.data;
    }

    /**
     * Generate user stories from a PRD using AI
     */
    async generateUserStories(request: GenerateUserStoriesRequest) {
        const response = await api.post('/user-stories/generate', request);
        return response.data;
    }

    /**
     * Create a user story manually
     */
    async createUserStory(data: Partial<UserStory>) {
        const response = await api.post('/user-stories', data);
        return response.data;
    }

    /**
     * Update a user story
     */
    async updateUserStory(id: string, data: Partial<UserStory>) {
        const response = await api.put(`/user-stories/${id}`, data);
        return response.data;
    }

    /**
     * Delete a user story
     */
    async deleteUserStory(id: string) {
        const response = await api.delete(`/user-stories/${id}`);
        return response.data;
    }

    /**
     * Generate tasks for a user story using AI
     */
    async generateTasks(userStoryId: string, options?: { extraContext?: string, regenerate?: boolean, count?: number }) {
        const response = await api.post(`/user-stories/${userStoryId}/generate-tasks`, options || {});
        return response.data;
    }

    /**
     * Create a task manually
     */
    async createTask(data: any) {
        const response = await api.post('/tasks', data);
        return response.data;
    }

    /**
     * Update a task
     */
    async updateTask(id: string, data: any) {
        const response = await api.put(`/tasks/${id}`, data);
        return response.data;
    }

    /**
     * Delete a task
     */
    async deleteTask(id: string) {
        const response = await api.delete(`/tasks/${id}`);
        return response.data;
    }
}

export const userStoryService = new UserStoryService();
